import { Controller } from "stimulus"

const STORAGE_KEY = "henri.sidebar.collapsed"
const AUTO_COLLAPSE_MAX = 1280
const DESKTOP_MIN = 992
const ROOT_LEVEL_CLASS = "root-level"
const ANIMATE_CLASS = "sidebar-animate"
const COLLAPSING_CLASS = "sidebar-collapsing"
const ANIMATE_MS = 200

export default class extends Controller {
  static targets = ["pin", "menu"]

  connect() {
    this.boundResize = this.onResize.bind(this)
    this.boundDocumentClick = this.onDocumentClick.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    this.flyoutCloseTimers = new WeakMap()
    this.wasBelowAutoCollapse = window.innerWidth < AUTO_COLLAPSE_MAX

    this.setupMenu()
    this.markActivePath()
    this.applyInitialCollapsedState()
    this.createRailTooltip()

    window.addEventListener("resize", this.boundResize)
    document.addEventListener("click", this.boundDocumentClick)
    document.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    window.removeEventListener("resize", this.boundResize)
    document.removeEventListener("click", this.boundDocumentClick)
    document.removeEventListener("keydown", this.boundKeydown)
    this.clearAnimateClass()
    this.clearCollapsedHeaderHeight()
    this.destroyRailTooltip()
  }

  get menu() {
    return this.hasMenuTarget ? this.menuTarget : this.element.querySelector("#main-menu")
  }

  get pin() {
    return this.hasPinTarget ? this.pinTarget : this.element.querySelector("#sidebar-pin-toggle")
  }

  isDesktop() {
    return window.innerWidth >= DESKTOP_MIN
  }

  isCollapsed() {
    return document.documentElement.classList.contains("sidebar-collapsed")
  }

  resolveInitialCollapsed() {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === "true") return true
    if (stored === "false") return false
    return window.innerWidth < AUTO_COLLAPSE_MAX
  }

  applyInitialCollapsedState() {
    if (this.isDesktop()) {
      this.applyCollapsed(this.resolveInitialCollapsed())
    } else {
      this.clearCollapsedClasses()
    }

    if (this.isDesktop() && this.isCollapsed()) {
      this.closeAllFlyouts()
    }
  }

  togglePin(event) {
    event.preventDefault()
    if (!this.isDesktop()) return

    const next = !this.isCollapsed()
    this.hideRailTooltip()

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.applyCollapsed(next)
      window.localStorage.setItem(STORAGE_KEY, String(next))
      return
    }

    if (next) {
      this.animateCollapse()
    } else {
      this.animateExpand()
    }
    window.localStorage.setItem(STORAGE_KEY, String(next))
  }

  onResize() {
    // Snap on resize — never carry a pin-click transition across a viewport change
    this.clearAnimateClass()

    const belowAutoCollapse = window.innerWidth < AUTO_COLLAPSE_MAX
    const crossedAutoBreakpoint = belowAutoCollapse !== this.wasBelowAutoCollapse
    this.wasBelowAutoCollapse = belowAutoCollapse

    if (crossedAutoBreakpoint) {
      window.localStorage.removeItem(STORAGE_KEY)
    }

    if (!this.isDesktop()) {
      // Leaving icon-rail for mobile accordion: clear flyouts, then reopen active parents
      if (this.isCollapsed()) {
        this.closeAllFlyouts()
        this.restoreActiveAccordions()
      }
      this.clearCollapsedClasses()
      return
    }

    if (crossedAutoBreakpoint) {
      this.applyCollapsed(belowAutoCollapse)
    } else {
      this.applyCollapsed(this.resolveInitialCollapsed())
    }
  }

  sidebarMenuEl() {
    return this.element.querySelector(".sidebar-menu") || this.element
  }

  // Collapse as a wipe: keep expanded layout, shrink outer width, then swap to rail styles.
  animateCollapse() {
    this.clearAnimateClass()
    this.closeAllFlyouts()

    const menu = this.sidebarMenuEl()
    document.documentElement.classList.add(ANIMATE_CLASS)
    // Force a frame at full width so the transition to 64px actually runs
    menu.offsetWidth
    document.documentElement.classList.add(COLLAPSING_CLASS)

    this.afterWidthTransition(menu, () => {
      this.applyCollapsed(true)
      this.clearAnimateClass()
    })
  }

  // Expand as a wipe: leave rail styles, lay out full-width inner, grow outer clip.
  animateExpand() {
    this.clearAnimateClass()

    const menu = this.sidebarMenuEl()
    document.documentElement.classList.add(ANIMATE_CLASS)
    menu.offsetWidth
    this.applyCollapsed(false)

    this.afterWidthTransition(menu, () => {
      this.clearAnimateClass()
    })
  }

  afterWidthTransition(menu, callback) {
    const onEnd = (event) => {
      if (event.target !== menu || event.propertyName !== "width") return
      menu.removeEventListener("transitionend", onEnd)
      if (this.animateTimeout) {
        window.clearTimeout(this.animateTimeout)
        this.animateTimeout = null
      }
      callback()
    }
    menu.addEventListener("transitionend", onEnd)
    this.animateTimeout = window.setTimeout(() => {
      menu.removeEventListener("transitionend", onEnd)
      this.animateTimeout = null
      callback()
    }, ANIMATE_MS + 50)
  }

  clearAnimateClass() {
    if (this.animateTimeout) {
      window.clearTimeout(this.animateTimeout)
      this.animateTimeout = null
    }
    document.documentElement.classList.remove(ANIMATE_CLASS, COLLAPSING_CLASS)
  }
  applyCollapsed(collapsed) {
    if (!this.isDesktop()) return

    if (collapsed) {
      this.closeAllFlyouts()
    } else if (this.isCollapsed()) {
      // Leaving icon-rail: clear flyout positioning, then reopen active parents
      this.closeAllFlyouts()
      this.restoreActiveAccordions()
    }

    document.documentElement.classList.toggle("sidebar-collapsed", collapsed)
    this.element.classList.toggle("sidebar-collapsed", collapsed)
    this.setPinA11y(collapsed)
    this.syncCollapsedHeaderHeight()
  }

  clearCollapsedClasses() {
    document.documentElement.classList.remove("sidebar-collapsed")
    this.element.classList.remove("sidebar-collapsed")
    this.clearCollapsedHeaderHeight()
  }

  syncCollapsedHeaderHeight() {
    if (!this.isDesktop() || !this.isCollapsed()) {
      this.clearCollapsedHeaderHeight()
      return
    }

    const header = document.getElementById("header")
    if (!header) {
      this.clearCollapsedHeaderHeight()
      return
    }

    document.documentElement.style.setProperty(
      "--sidebar-collapsed-header-height",
      `${header.offsetHeight}px`
    )
  }

  clearCollapsedHeaderHeight() {
    document.documentElement.style.removeProperty("--sidebar-collapsed-header-height")
  }

  setPinA11y(collapsed) {
    if (!this.pin) return
    const label = collapsed ? "Expand sidebar" : "Collapse sidebar"
    this.pin.setAttribute("aria-label", label)
    this.pin.setAttribute("title", label)
    this.pin.setAttribute("aria-pressed", String(collapsed))
  }

  setupMenu() {
    const menu = this.menu
    if (!menu) return

    Array.from(menu.children).forEach((item) => {
      if (item.matches("li")) item.classList.add(ROOT_LEVEL_CLASS)
    })

    menu.querySelectorAll("li").forEach((item) => {
      const subMenu = item.querySelector(":scope > ul")
      if (!subMenu) return

      item.classList.add("has-sub")
      const link = item.querySelector(":scope > a")
      if (!link) return

      link.addEventListener("click", (event) => this.onSubmenuClick(event, item, subMenu))
      item.addEventListener("mouseenter", () => this.onSubmenuEnter(item, subMenu))
      item.addEventListener("mouseleave", () => this.onSubmenuLeave(item, subMenu))
    })

    menu.querySelectorAll(":scope > li > a").forEach((link) => {
      const labelEl = Array.from(link.querySelectorAll(":scope > span")).find(
        (span) => !span.classList.contains("badge") && span.id !== "amenity-badge-sidebar"
      )
      const label = labelEl ? labelEl.textContent.trim() : ""
      if (label.length) {
        link.setAttribute("data-sidebar-label", label)
        link.removeAttribute("title")
      }

      link.addEventListener("mouseenter", () => this.showRailTooltip(link))
      link.addEventListener("mouseleave", () => this.hideRailTooltip())
    })
  }

  onSubmenuClick(event, item, subMenu) {
    event.preventDefault()

    if (this.isDesktop() && this.isCollapsed()) {
      const wasOpen = item.classList.contains("opened") && subMenu.classList.contains("visible")
      if (wasOpen) {
        this.closeFlyout(item, subMenu)
      } else {
        this.openFlyout(item, subMenu)
      }
      return
    }

    if (item.classList.contains(ROOT_LEVEL_CLASS)) {
      this.menu.querySelectorAll(`.${ROOT_LEVEL_CLASS}`).forEach((other) => {
        if (other === item) return
        const otherSub = other.querySelector(":scope > ul.visible")
        if (otherSub) this.collapseMenu(otherSub)
      })
    }

    if (!item.classList.contains("opened")) {
      this.expandMenu(subMenu)
    } else {
      this.collapseMenu(subMenu)
    }
  }

  onSubmenuEnter(item, subMenu) {
    if (!(this.isDesktop() && this.isCollapsed())) return
    const timer = this.flyoutCloseTimers.get(item)
    if (timer) clearTimeout(timer)
    this.hideRailTooltip()
    this.openFlyout(item, subMenu)
  }

  onSubmenuLeave(item, subMenu) {
    if (!(this.isDesktop() && this.isCollapsed())) return
    const timer = this.flyoutCloseTimers.get(item)
    if (timer) clearTimeout(timer)
    this.flyoutCloseTimers.set(
      item,
      setTimeout(() => this.closeFlyout(item, subMenu), 150)
    )
  }

  openFlyout(item, subMenu) {
    this.menu.querySelectorAll(`.${ROOT_LEVEL_CLASS}`).forEach((other) => {
      if (other === item) return
      const otherSub = other.querySelector(":scope > ul")
      if (otherSub) this.closeFlyout(other, otherSub)
    })

    this.ensureFlyoutTitle(item, subMenu)
    item.classList.add("opened")
    const rect = item.getBoundingClientRect()
    subMenu.classList.add("visible")
    Object.assign(subMenu.style, {
      position: "fixed",
      top: `${rect.top}px`,
      left: `${rect.right + 4}px`,
      margin: "0",
      display: "block",
    })
  }

  ensureFlyoutTitle(item, subMenu) {
    const link = item.querySelector(":scope > a")
    const label = link?.getAttribute("data-sidebar-label")?.trim() || ""
    let title = subMenu.querySelector(":scope > .sidebar-flyout-title")

    if (!label) {
      if (title) title.remove()
      return
    }

    if (!title) {
      title = document.createElement("li")
      title.className = "sidebar-flyout-title"
      title.setAttribute("aria-hidden", "true")
      subMenu.insertBefore(title, subMenu.firstChild)
    }

    title.textContent = label
  }

  closeFlyout(item, subMenu) {
    item.classList.remove("opened")
    subMenu.classList.remove("visible")
    subMenu.style.position = ""
    subMenu.style.top = ""
    subMenu.style.left = ""
    subMenu.style.margin = ""
    subMenu.style.display = ""
    subMenu.querySelector(":scope > .sidebar-flyout-title")?.remove()
  }

  closeAllFlyouts() {
    if (!this.menu) return
    this.menu.querySelectorAll("li.has-sub").forEach((item) => {
      const subMenu = item.querySelector(":scope > ul")
      if (subMenu) this.closeFlyout(item, subMenu)
    })
  }

  // Re-open accordion parents that contain the current page's active item
  restoreActiveAccordions() {
    if (!this.menu) return

    this.menu.querySelectorAll("li.active.has-sub").forEach((item) => {
      const subMenu = item.querySelector(":scope > ul")
      if (!subMenu) return

      item.classList.add("opened")
      subMenu.classList.add("visible")
      subMenu.style.position = ""
      subMenu.style.top = ""
      subMenu.style.left = ""
      subMenu.style.margin = ""
      subMenu.style.display = ""
    })
  }

  expandMenu(subMenu) {
    const $ = window.jQuery || window.$
    subMenu.parentElement.classList.add("opened")
    if ($ && $.fn.slideDown) {
      // Keep .visible off until the slide finishes so CSS display:block
      // does not cancel jQuery's height animation.
      $(subMenu).stop(true, true).slideDown(500, () => {
        subMenu.classList.add("visible")
        subMenu.style.display = ""
      })
    } else {
      subMenu.classList.add("visible")
      subMenu.style.display = "block"
    }
  }

  collapseMenu(subMenu) {
    const $ = window.jQuery || window.$
    subMenu.parentElement.classList.remove("opened")
    if ($ && $.fn.slideUp) {
      // Do not remove .visible until after slideUp — sections opened via CSS
      // (page load / restoreActiveAccordions) have no inline display, so
      // removing .visible immediately sets display:none and skips the animation.
      $(subMenu).stop(true, true).slideUp(500, () => {
        subMenu.classList.remove("visible")
        subMenu.style.display = ""
      })
    } else {
      subMenu.classList.remove("visible")
      subMenu.style.display = "none"
    }
  }

  onDocumentClick(event) {
    if (!(this.isDesktop() && this.isCollapsed())) return
    if (event.target.closest("#main-menu > li.has-sub")) return
    if (event.target.closest("#sidebar-pin-toggle")) return
    this.closeAllFlyouts()
  }

  onKeydown(event) {
    if (event.key !== "Escape" && event.keyCode !== 27) return
    if (!(this.isDesktop() && this.isCollapsed())) return
    this.closeAllFlyouts()
  }

  createRailTooltip() {
    this.railTooltip = document.createElement("div")
    this.railTooltip.id = "sidebar-rail-tooltip"
    this.railTooltip.className = "sidebar-rail-tooltip"
    this.railTooltip.setAttribute("role", "tooltip")
    this.railTooltip.hidden = true
    document.body.appendChild(this.railTooltip)
  }

  destroyRailTooltip() {
    if (this.railTooltip && this.railTooltip.parentNode) {
      this.railTooltip.parentNode.removeChild(this.railTooltip)
    }
    this.railTooltip = null
  }

  showRailTooltip(link) {
    if (!(this.isDesktop() && this.isCollapsed())) return
    if (link.closest("li")?.classList.contains("has-sub")) return
    const label = link.getAttribute("data-sidebar-label")
    if (!label || !this.railTooltip) return

    const rect = link.getBoundingClientRect()
    this.railTooltip.textContent = label
    this.railTooltip.style.top = `${rect.top + rect.height / 2}px`
    this.railTooltip.style.left = `${rect.right + 4}px`
    this.railTooltip.hidden = false
    this.railTooltip.style.display = "block"
  }

  hideRailTooltip() {
    if (!this.railTooltip) return
    this.railTooltip.hidden = true
    this.railTooltip.style.display = "none"
    this.railTooltip.textContent = ""
  }

  markActivePath() {
    if (!this.menu) return
    const lpath = location.pathname

    this.menu.querySelectorAll("li").forEach((navItem) => {
      const link = navItem.querySelector("a")
      if (!link) return
      const rpath = link.getAttribute("href")
      if (!rpath) return

      if (lpath.includes(rpath) || (lpath.includes("/complaints") && rpath.includes("/chargebacks"))) {
        if (rpath === "/payments" && lpath.includes("/analytics")) return
        if (rpath === "/classes" && !lpath.includes("new") && !lpath.includes("edit") && rpath !== lpath) {
          return
        }

        navItem.classList.add("active")
        if (navItem.parentElement) navItem.parentElement.classList.add("visible")
        if (navItem.parentElement?.parentElement) {
          const parentLi = navItem.parentElement.parentElement
          parentLi.classList.add("active", "opened")
        }
        const greatGrandparent = navItem.parentElement?.parentElement?.parentElement
        if (greatGrandparent) {
          greatGrandparent.classList.add("visible")
          greatGrandparent.parentElement?.classList.add("active", "opened")
        }
      } else if (lpath === "/pending-comments" && rpath === "/pending-posts") {
        navItem.classList.add("active", "opened")
      } else if (lpath === "/flagged-comments" && rpath === "/flagged-posts") {
        navItem.classList.add("active", "opened")
      } else if (lpath === "/delivery-service" && rpath === "/packages") {
        navItem.classList.add("active", "opened")
      }
    })
  }
}
